import { useState } from "react";
import { BadgeCheck, FileText, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Kulaté razítko osvědčení — autentický inkoustový otisk: hrubé zrno, skvrnky,
// nepravidelné přerušené kružnice, místy bledší písmena. Barvy řeší .stamp-* v index.css.
const CertStamp = ({ uid }) => (
  <div
    className="absolute -bottom-14 -right-4 w-[118px] h-[118px] md:-bottom-16 md:-right-8 md:w-36 md:h-36 rotate-12 pointer-events-none select-none"
    aria-hidden="true"
  >
    <svg viewBox="0 0 200 200" className="w-full h-full">
      <defs>
        <path id={`${uid}-ring`} d="M 26,100 a 74,74 0 1 1 148,0 a 74,74 0 1 1 -148,0" />
        <filter id={`${uid}-strong`} x="-15%" y="-15%" width="130%" height="130%">
          <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="3" seed="4" result="grain" />
          <feDisplacementMap in="SourceGraphic" in2="grain" scale="3.4" result="rough" />
          <feTurbulence type="fractalNoise" baseFrequency="0.05" numOctaves="3" seed="11" result="blotch" />
          <feComponentTransfer in="blotch" result="fade">
            <feFuncA type="linear" slope="2.3" intercept="-0.5" />
          </feComponentTransfer>
          <feComposite in="rough" in2="fade" operator="in" />
        </filter>
        <filter id={`${uid}-soft`} x="-15%" y="-15%" width="130%" height="130%">
          <feTurbulence type="fractalNoise" baseFrequency="0.6" numOctaves="2" seed="8" result="grain" />
          <feDisplacementMap in="SourceGraphic" in2="grain" scale="1.4" result="rough" />
          <feTurbulence type="fractalNoise" baseFrequency="0.06" numOctaves="3" seed="21" result="blotch" />
          <feComponentTransfer in="blotch" result="fade">
            <feFuncA type="linear" slope="1.55" intercept="-0.22" />
          </feComponentTransfer>
          <feComposite in="rough" in2="fade" operator="in" />
        </filter>
      </defs>

      {/* kružnice + inkoustové skvrnky — silnější otisk */}
      <g filter={`url(#${uid}-strong)`} opacity="0.9">
        <circle cx="100" cy="100" r="94" fill="none" strokeWidth="2.4" className="stamp-ink" />
        <circle cx="100" cy="100" r="87" fill="none" strokeWidth="1.1" className="stamp-ink" />
        <circle cx="100" cy="100" r="44" fill="none" strokeWidth="1" className="stamp-gold" />
        <circle cx="168" cy="58" r="2.2" className="stamp-ink-fill" opacity="0.7" />
        <circle cx="178" cy="92" r="1.3" className="stamp-ink-fill" opacity="0.5" />
        <circle cx="172" cy="141" r="2.6" className="stamp-ink-fill" opacity="0.8" />
        <circle cx="150" cy="178" r="1.5" className="stamp-ink-fill" opacity="0.45" />
        <circle cx="108" cy="189" r="1.9" className="stamp-ink-fill" opacity="0.65" />
        <circle cx="52" cy="182" r="1.2" className="stamp-ink-fill" opacity="0.4" />
        <circle cx="26" cy="148" r="2.3" className="stamp-ink-fill" opacity="0.7" />
        <circle cx="17" cy="104" r="1.4" className="stamp-ink-fill" opacity="0.5" />
        <circle cx="29" cy="61" r="1.8" className="stamp-ink-fill" opacity="0.6" />
        <circle cx="59" cy="31" r="1.5" className="stamp-ink-fill" opacity="0.45" />
        <circle cx="120" cy="15" r="2" className="stamp-ink-fill" opacity="0.6" />
        <circle cx="150" cy="35" r="1.1" className="stamp-ink-fill" opacity="0.4" />
      </g>

      {/* text + zlatý detail — jemnější opotřebení, čitelnost zachována */}
      <g filter={`url(#${uid}-soft)`} opacity="0.95">
        <text fontSize="12.5" letterSpacing="3" className="stamp-ink-text" style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
          <textPath href={`#${uid}-ring`} startOffset="25%" textAnchor="middle">
            ODBORNÁ ZPŮSOBILOST&#160;&#160;·&#160;&#160;ODBORNÁ ZKOUŠKA
          </textPath>
        </text>
        <circle cx="100" cy="174" r="3" className="stamp-gold-fill" />
        <path
          d="M 84 100 l 11 11 l 22 -24"
          fill="none"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="stamp-gold"
        />
      </g>
    </svg>
  </div>
);

// Nové osvědčení přidáte jednoduše další položkou do pole CERTIFICATIONS — design se nemění.
const CERTIFICATIONS = [
  {
    testId: "cert-efpa-uvery-2026",
    title: "Odborná zkouška ze spotřebitelského úvěru",
    date: "27. 10. 2026",
    scope: "Spotřebitelský úvěr na bydlení a spotřebitelský úvěr jiný než na bydlení",
    law: "Zákon č. 257/2016 Sb., o spotřebitelském úvěru",
    issuer: "Zkoušky EFPA ČR — Evropská asociace finančního plánování Česká republika",
    pdf: "/docs/osvedceni-efpa-uvery-2026.pdf",
  },
  {
    testId: "cert-efpa-2026",
    title: "Odborná zkouška z distribuce pojištění",
    date: "21. 10. 2026",
    scope: "Životní pojištění a neživotní občanské pojištění",
    law: "Zákon č. 170/2018 Sb., o distribuci pojištění a zajištění",
    issuer: "Zkoušky EFPA ČR — Evropská asociace finančního plánování Česká republika",
    pdf: "/docs/osvedceni-efpa-2026.pdf",
  },
];

// Starší certifikáty a ocenění — doklady profesní historie, ne aktuální oprávnění.
// Nový doklad přidáte položkou do pole AWARDS (náhledy i plné velikosti jsou v /docs/awards/).
const AWARDS = [
  { slug: "axa-osvedceni-2013", title: "Osvědčení o odborné způsobilosti — AXA", year: "2013" },
  { slug: "axa-investicni-2014", title: "Investiční certifikát — AXA", year: "2014" },
  { slug: "hypocentrum-skoleni-2015", title: "Základní školení — M&M Hypocentrum", year: "2015" },
  { slug: "cpp-skoleni-2015", title: "Odborné školení — ČPP", year: "2015" },
  { slug: "mm-dekret-manazer-2016", title: "Jmenování oblastním manažerem — M&M Finance", year: "2016" },
  { slug: "mm-top-pojistovaci-10-2017", title: "TOP obchodník · pojišťovací poradce, 10. místo", year: "2017" },
  { slug: "mm-top-financni-6-2017", title: "TOP obchodník · finanční poradce, 6. místo", year: "2017" },
  { slug: "mm-top-pojistovaci-3-2017", title: "TOP obchodník · pojišťovací poradce, 3. místo", year: "2017" },
];

const AwardsGallery = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  return (
    <div className="mt-20 md:mt-24" data-testid="awards-block">
      <Reveal>
        <div className="text-center">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            data-testid="awards-toggle"
            className="inline-flex items-center gap-3 border border-[#B69A58] bg-white px-6 py-4 text-xs uppercase tracking-widest font-medium text-[#252525] hover:bg-[#FAF6EC] transition-colors duration-300"
          >
            {open ? "Skrýt další certifikáty a ocenění" : "Zobrazit další certifikáty a ocenění"}
            <ChevronDown
              className={`h-4 w-4 text-[#B69A58] transition-transform duration-300 ${open ? "rotate-180" : ""}`}
              strokeWidth={1.5}
              aria-hidden="true"
            />
          </button>
          <p className="text-xs text-[#737373] mt-4">Doklady profesní historie — dřívější školení a ocenění z let 2013–2017.</p>
        </div>
      </Reveal>

      {open && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-10" data-testid="awards-grid">
          {AWARDS.map((award) => (
            <button
              key={award.slug}
              type="button"
              onClick={() => setActive(award)}
              data-testid={`award-${award.slug}`}
              className="group text-left border border-[#F0F0F0] bg-white p-3 hover:border-[#B69A58] transition-colors duration-300"
            >
              <img
                src={`/docs/awards/${award.slug}-thumb.webp`}
                alt={award.title}
                loading="lazy"
                className="w-full aspect-[3/4] object-cover object-top border border-[#F0F0F0]"
              />
              <p className="text-xs md:text-sm font-medium text-[#252525] leading-snug mt-3">{award.title}</p>
              <p className="text-xs text-[#B69A58] mt-1">{award.year}</p>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-white sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="award-lightbox">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-[#252525]">{active.title}</DialogTitle>
                <DialogDescription className="text-sm text-[#737373]">
                  {active.year} · doklad profesní historie
                </DialogDescription>
              </DialogHeader>
              <img
                src={`/docs/awards/${active.slug}.webp`}
                alt={active.title}
                className="w-full h-auto border border-[#F0F0F0]"
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


export const Certifications = () => {
  return (
    <section id="odbornost" data-testid="certifications-section" className="py-24 md:py-32 border-t border-[#F0F0F0] bg-[#FAFAF8] overflow-x-clip">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Odbornost a certifikace
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-[#252525] mt-6 max-w-3xl">
            Odborná kvalifikace pro finanční poradenství
          </h2>
          <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-8 max-w-2xl">
            K poradenství v Ústí nad Labem přistupuji odborně — k hypotékám i úvěrům
            i k pojištění. Níže najdete přehled mých odborných zkoušek a osvědčení,
            každé si můžete otevřít a ověřit.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-12 md:mt-16">
          {CERTIFICATIONS.map((cert, i) => (
            <Reveal key={cert.testId} delay={i * 0.08}>
              <article
                data-testid={cert.testId}
                className="relative h-full bg-white border border-[#F0F0F0] border-l-2 border-l-[#B69A58] p-8 md:p-10 flex flex-col"
              >
                <CertStamp uid={cert.testId} />
                <div className="flex items-start gap-4">
                  <span className="shrink-0 w-11 h-11 rounded-full bg-[#FAF6EC] flex items-center justify-center" aria-hidden="true">
                    <BadgeCheck className="h-5 w-5 text-[#B69A58]" strokeWidth={1.5} />
                  </span>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl text-[#252525] leading-snug">{cert.title}</h3>
                    <p className="text-xs uppercase tracking-widest text-[#B69A58] font-medium mt-2">Absolvováno {cert.date}</p>
                  </div>
                </div>

                <dl className="mt-8 space-y-4 text-sm leading-relaxed">
                  <div>
                    <dt className="text-xs uppercase tracking-widest text-[#737373] font-medium">Oblast odbornosti</dt>
                    <dd className="text-[#252525] mt-1">{cert.scope}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-widest text-[#737373] font-medium">Právní základ</dt>
                    <dd className="text-[#4A4A4A] mt-1">{cert.law}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-widest text-[#737373] font-medium">Vydáno v rámci</dt>
                    <dd className="text-[#4A4A4A] mt-1">{cert.issuer}</dd>
                  </div>
                </dl>

                <a
                  href={cert.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${cert.testId}-view`}
                  className="mt-auto pt-8 inline-flex items-center gap-3 text-xs uppercase tracking-widest font-medium text-[#252525] hover:text-[#B69A58] transition-colors duration-300"
                >
                  <FileText className="h-4 w-4" strokeWidth={1.5} />
                  Zobrazit osvědčení
                </a>
              </article>
            </Reveal>
          ))}
        </div>

        <AwardsGallery />
      </div>
    </section>
  );
};
